import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertCircle,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  KeyRound,
  Landmark,
  Link2,
  Mail,
  Package,
  Phone,
  Umbrella,
  UserPen,
  UserX,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { LeaveBalance, RoleCode } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { leaveService } from '@/services/leave.service';
import { attendanceService } from '@/services/attendance.service';
import { payrollService } from '@/services/payroll.service';
import { assetsService } from '@/services/assets.service';
import { staffDocumentsService } from '@/services/staff-documents.service';
import { notificationsService } from '@/services/notifications.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatTile } from '@/components/ui/stat-tile';
import { SectionHeader } from '@/components/ui/section-header';

// ── Helpers ────────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<RoleCode, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  TEAM_LEADER: 'Team Leader',
  USER: 'Employee',
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Every card body uses the same padding so sections line up across the grid. */
const CARD_BODY_CLS = 'flex-1 p-3 sm:p-4';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso + (iso.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function formatHours(mins: number): string {
  if (!mins || mins <= 0) return '0h';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Day counts arrive as NUMERIC strings ("1.00"); show 1 → "1", 1.5 → "1.5". */
function formatDays(n: number): string {
  return String(Math.round(n * 100) / 100);
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

// ── Skeleton primitive ────────────────────────────────────────────────────────

function Sk({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-muted/50', className)} />;
}

// ── Glassmorphism card wrapper ─────────────────────────────────────────────────

/** Fills its grid cell, so cards sharing a row always end at the same height. */
function GlassCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn(
      'flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 dark:border-border/50',
      'bg-card/90 dark:bg-card shadow-sm backdrop-blur-sm',
      className,
    )}>
      {children}
    </div>
  );
}

// ── Header link — every card keeps its navigation in the header, never a footer ──

function HeaderLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-primary hover:text-primary" onClick={onClick}>
      {label}
    </Button>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────

function ErrorBanner({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <span className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Failed to load
      </span>
      <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

// ── 1. Profile Hero ───────────────────────────────────────────────────────────

interface ProfileHeroProps {
  user: ReturnType<typeof useAuth>['user'];
  profile: ReturnType<typeof useAuth>['profile'];
  photo_url: string | null;
  roles: RoleCode[];
  navigate: ReturnType<typeof useNavigate>;
}

function ProfileHeroCard({ user, profile, photo_url, roles, navigate }: ProfileHeroProps) {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '—';
  const initials = (() => {
    const f = user?.first_name?.trim()[0] ?? '';
    const l = user?.last_name?.trim()[0] ?? '';
    return (f + l).toUpperCase() || '?';
  })();
  const avatarUrl = photo_url ?? profile?.avatar_url ?? null;

  const contact: { icon: LucideIcon; text: string }[] = [];
  if (user?.email) contact.push({ icon: Mail, text: user.email });
  if (user?.phone) contact.push({ icon: Phone, text: user.phone });
  if (profile?.joining_date) contact.push({ icon: CalendarDays, text: `Joined ${formatDate(profile.joining_date)}` });
  if (user?.last_login_at) contact.push({ icon: Clock, text: `Last login ${timeAgo(user.last_login_at)}` });

  return (
    <GlassCard>
      <div className="h-1 bg-gradient-to-r from-teal-500 via-teal-400 to-emerald-400" />

      <div className="p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border-2 border-white shadow-sm dark:border-border">
            {avatarUrl ? (
              <img src={avatarUrl} alt={fullName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-teal-600 text-lg font-bold text-white">
                {initials}
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h2 className="truncate text-lg font-bold leading-tight text-slate-800 dark:text-slate-100">{fullName}</h2>
              {user?.employee_code && (
                <span className="text-xs text-slate-400 dark:text-slate-500">#{user.employee_code}</span>
              )}
              <Badge variant={user?.is_active ? 'default' : 'secondary'} className="text-[10px]">
                {user?.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {roles.map((r) => (
                <Badge key={r} variant="outline" className="text-[10px] font-medium">
                  {ROLE_LABELS[r] ?? r}
                </Badge>
              ))}
            </div>

            {(user?.department_name || user?.designation_name) && (
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-slate-600 dark:text-slate-300">
                {user.department_name && (
                  <span className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-slate-400" />
                    {user.department_name}
                  </span>
                )}
                {user.designation_name && (
                  <span className="flex items-center gap-1.5">
                    <BriefcaseBusiness className="h-3.5 w-3.5 text-slate-400" />
                    {user.designation_name}
                  </span>
                )}
              </div>
            )}
          </div>

          <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate('/account/manage')}>
            <UserPen className="h-3.5 w-3.5" />
            Edit Profile
          </Button>
        </div>

        {contact.length > 0 && (
          <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 border-t border-border/40 pt-3 sm:grid-cols-2 xl:grid-cols-4">
            {contact.map(({ icon: Icon, text }) => (
              <span key={text} className="flex min-w-0 items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="truncate">{text}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 2. Leave Balances ─────────────────────────────────────────────────────────

/** Tiles stretch to fill the row, so two leave types never leave an empty third slot. */
const BALANCE_GRID_CLS = 'grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-2 sm:gap-3';

function LeaveBalanceTile({ balance }: { balance: LeaveBalance }) {
  const available = Number(balance.available ?? 0);
  // The type colour marks identity only (the dot); the number stays in the text colour
  // so light type colours such as amber keep readable contrast on the light theme.
  const color = balance.leave_type_color || 'hsl(var(--primary))';

  return (
    <div className="rounded-xl border border-border/60 bg-background/60 p-3 dark:bg-muted/20">
      <div className="flex items-start gap-2">
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden />
        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground" title={balance.leave_type_name}>
          {balance.leave_type_name}
        </p>
      </div>

      <p className="mt-2 flex items-baseline gap-1.5">
        <span className={cn('text-2xl font-bold leading-none tabular-nums', available < 0 ? 'text-destructive' : 'text-foreground')}>
          {formatDays(available)}
        </span>
        <span className="text-sm text-muted-foreground">{available === 1 ? 'day left' : 'days left'}</span>
      </p>
    </div>
  );
}

function LeaveBalancesSection({
  query,
  navigate,
}: {
  query: ReturnType<typeof useQuery>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { has } = usePermission();
  const balances = query.data as Awaited<ReturnType<typeof leaveService.getMyBalances>> | undefined;
  const paid = (balances ?? []).filter((b) => b.is_paid);
  // Unpaid types never carry a balance (always 0), so a "0 / 0 used" tile would mislead.
  const unpaid = (balances ?? []).filter((b) => !b.is_paid);

  return (
    <GlassCard>
      <SectionHeader
        icon={<Umbrella />}
        title="Leave Balances"
        action={has(PERMISSIONS.LEAVE_VIEW_OWN) ? <HeaderLink label="Go to Leave" onClick={() => navigate('/leave')} /> : undefined}
      />
      <div className={CARD_BODY_CLS}>
        {query.isError ? (
          <ErrorBanner onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div className={BALANCE_GRID_CLS}>
            {[1, 2].map((i) => <Sk key={i} className="h-[84px] rounded-xl" />)}
          </div>
        ) : !balances || balances.length === 0 ? (
          <p className="text-sm text-muted-foreground">No leave balance for this year yet.</p>
        ) : (
          <div className="space-y-2">
            {paid.length > 0 && (
              <div className={BALANCE_GRID_CLS}>
                {paid.map((b) => <LeaveBalanceTile key={b.leave_type_id} balance={b} />)}
              </div>
            )}
            {unpaid.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {unpaid.map((b) => b.leave_type_name).join(', ')} {unpaid.length === 1 ? 'is' : 'are'} unpaid,
                so there is no balance to track.
              </p>
            )}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 3a. Attendance Summary ────────────────────────────────────────────────────

interface AttendanceStats {
  presentDays: number;
  absentDays: number;
  lateDays: number;
  totalMinutes: number;
}

function AttendanceSummaryCard({
  query,
  stats,
  monthLabel,
  navigate,
}: {
  query: ReturnType<typeof useQuery>;
  stats: AttendanceStats;
  monthLabel: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  // 4 across when the card is wide (full-width phone landscape, 2-of-3 columns on desktop),
  // 2 × 2 when it sits in a half-width tablet column.
  const tilesCls = 'grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-2 xl:grid-cols-4';

  return (
    <GlassCard>
      <SectionHeader
        icon={<CalendarDays />}
        title="Attendance"
        action={
          <>
            <span className="text-xs font-normal text-muted-foreground">{monthLabel}</span>
            <HeaderLink label="View Calendar" onClick={() => navigate('/account/attendance')} />
          </>
        }
      />
      <div className={CARD_BODY_CLS}>
        {query.isError ? (
          <ErrorBanner onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div className={tilesCls}>
            {[1, 2, 3, 4].map((i) => <Sk key={i} className="h-[76px] rounded-xl" />)}
          </div>
        ) : (
          <div className={tilesCls}>
            <StatTile label="Present" value={stats.presentDays} sub="days" icon={CheckCircle2} tone="emerald" />
            <StatTile label="Hours" value={formatHours(stats.totalMinutes)} icon={Clock} tone="violet" />
            <StatTile label="Late" value={stats.lateDays} sub="days" icon={AlertCircle} tone="amber" />
            <StatTile label="Absent" value={stats.absentDays} sub="days" icon={UserX} tone="rose" />
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 3b. Recent Leave Requests ─────────────────────────────────────────────────

const LEAVE_STATUS_STYLES = {
  pending:   'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400',
  approved:  'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400',
  rejected:  'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400',
  cancelled: 'bg-muted text-muted-foreground border-border',
} as const;

function RecentLeaveRequestsCard({
  query,
  navigate,
}: {
  query: ReturnType<typeof useQuery>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const requests = query.data as Awaited<ReturnType<typeof leaveService.listMyRequests>> | undefined;
  const items = requests?.data ?? [];

  return (
    <GlassCard>
      <SectionHeader
        icon={<CalendarDays />}
        title="Recent Leaves"
        action={<HeaderLink label="View All" onClick={() => navigate('/leave')} />}
      />
      <div className={CARD_BODY_CLS}>
        {query.isError ? (
          <ErrorBanner onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Sk key={i} className="h-9 rounded-lg" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No leave requests yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map((req) => {
              const statusCls = LEAVE_STATUS_STYLES[req.status] ?? LEAVE_STATUS_STYLES.cancelled;
              const days = Number(req.days_count ?? 0);
              const dates = req.start_date === req.end_date
                ? formatDate(req.start_date)
                : `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`;
              // One line per request: the name truncates first so dates and status stay readable.
              return (
                <div key={req.id} className="flex items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-2">
                  {req.leave_type_color && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: req.leave_type_color }}
                    />
                  )}
                  <span
                    className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700 dark:text-slate-200"
                    title={req.leave_type_name ?? 'Leave'}
                  >
                    {req.leave_type_name ?? 'Leave'}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                    {dates}
                    {days > 0 && ` (${formatDays(days)} ${days === 1 ? 'day' : 'days'})`}
                  </span>
                  <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold', statusCls)}>
                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 4. Salary Snapshot ────────────────────────────────────────────────────────

function SalarySnapshotCard({
  query,
  navigate,
}: {
  query: ReturnType<typeof useQuery>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const history = query.data as Awaited<ReturnType<typeof payrollService.getMySalaryHistory>> | undefined;
  const latest = history?.[0] ?? null;

  return (
    <GlassCard>
      <SectionHeader
        icon={<Wallet />}
        title="Salary Snapshot"
        action={<HeaderLink label="Salary History" onClick={() => navigate('/account/salary')} />}
      />
      <div className={CARD_BODY_CLS}>
        {query.isError ? (
          <ErrorBanner onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div className="rounded-xl bg-gradient-to-br from-teal-700 to-emerald-600 px-4 py-3">
            <Sk className="h-3 w-20 bg-white/20" />
            <Sk className="mt-2 h-7 w-32 bg-white/20" />
            <Sk className="mt-2 h-3 w-40 bg-white/20" />
          </div>
        ) : !latest ? (
          <p className="text-sm text-muted-foreground">No salary record computed yet.</p>
        ) : (
          <div className="rounded-xl bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 px-4 py-3 text-white shadow-sm">
            <p className="text-xs font-medium text-teal-100">
              Net pay, {MONTH_NAMES[(latest.month ?? 1) - 1]} {latest.year}
            </p>
            <p className="mt-0.5 text-2xl font-bold tabular-nums">{formatCurrency(latest.net_amount)}</p>
            <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-teal-100">
              <span>Gross {formatCurrency(latest.gross_amount)}</span>
              <span>
                Deductions {formatCurrency(
                  parseFloat(String(latest.gross_amount)) - parseFloat(String(latest.net_amount)),
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 5. Assigned Assets ────────────────────────────────────────────────────────

function AssignedAssetsCard({
  query,
  navigate,
}: {
  query: ReturnType<typeof useQuery>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const data = query.data as Awaited<ReturnType<typeof assetsService.listMyAssets>> | undefined;
  const items = data?.data ?? [];
  const total = data?.pagination.total ?? 0;

  return (
    <GlassCard>
      <SectionHeader
        icon={<Package />}
        title="Assigned Assets"
        action={
          <>
            {total > 0 && <Badge variant="secondary" className="text-xs">{total} total</Badge>}
            <HeaderLink label="View All" onClick={() => navigate('/account/assets')} />
          </>
        }
      />
      <div className={CARD_BODY_CLS}>
        {query.isError ? (
          <ErrorBanner onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Sk key={i} className="h-9 rounded-lg" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No assets assigned to you.</p>
        ) : (
          <div className="space-y-2">
            {items.map((asset) => {
              const category = [asset.category?.name, asset.subcategory?.name].filter(Boolean).join(' / ') || '—';
              // One line per asset: the category truncates first so code and status stay readable.
              return (
                <div key={asset.id} className="flex items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-2">
                  <code className="shrink-0 whitespace-nowrap text-xs font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {asset.asset_code}
                  </code>
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={category}>
                    {category}
                  </span>
                  {asset.status && (
                    <span
                      className="flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                      style={{ backgroundColor: asset.status.color }}
                    >
                      {asset.status.name}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 6. My Documents ───────────────────────────────────────────────────────────

function MyDocumentsCard({ query }: { query: ReturnType<typeof useQuery> }) {
  const docs = query.data as Awaited<ReturnType<typeof staffDocumentsService.list>> | undefined;

  return (
    <GlassCard>
      <SectionHeader icon={<FileText />} title="My Documents" />
      <div className={CARD_BODY_CLS}>
        {query.isError ? (
          <ErrorBanner onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Sk key={i} className="h-11 rounded-lg" />)}
          </div>
        ) : !docs || docs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-2">
            {docs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/20 px-3 py-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                    {doc.label || doc.original_name}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDate(doc.created_at)}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 shrink-0 p-0"
                  title="Download"
                  aria-label={`Download ${doc.label || doc.original_name}`}
                  onClick={() => staffDocumentsService.download(doc.id, doc.original_name, '/staff-documents/me')}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 7. Recent Notifications ───────────────────────────────────────────────────

function RecentNotificationsCard({
  query,
  onMarkAllRead,
}: {
  query: ReturnType<typeof useQuery>;
  onMarkAllRead: () => void;
}) {
  const data = query.data as Awaited<ReturnType<typeof notificationsService.list>> | undefined;
  const items = data?.data ?? [];

  return (
    <GlassCard>
      <SectionHeader
        icon={<Bell />}
        title="Notifications"
        action={items.some((n) => !n.is_read) ? <HeaderLink label="Mark all read" onClick={onMarkAllRead} /> : undefined}
      />
      <div className={CARD_BODY_CLS}>
        {query.isError ? (
          <ErrorBanner onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Sk key={i} className="h-11 rounded-lg" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notifications.</p>
        ) : (
          <div className="space-y-1.5">
            {items.map((notif) => (
              <div
                key={notif.id}
                className={cn(
                  'flex items-start gap-3 rounded-lg px-3 py-2 transition-colors',
                  notif.is_read
                    ? 'bg-muted/20'
                    : 'bg-primary/5 dark:bg-primary/10',
                )}
              >
                {!notif.is_read && (
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                )}
                <div className={cn('min-w-0 flex-1', notif.is_read && 'pl-[18px]')}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="line-clamp-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                      {notif.title}
                    </p>
                    <span className="shrink-0 text-[10px] text-muted-foreground">{timeAgo(notif.created_at)}</span>
                  </div>
                  {notif.body && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{notif.body}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 8. Quick Links ────────────────────────────────────────────────────────────

interface QuickLink { label: string; icon: React.ElementType; path: string; color: string }

const QUICK_LINKS: QuickLink[] = [
  { label: 'Edit Profile',      icon: UserPen,    path: '/account/manage',    color: 'text-violet-600 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400' },
  { label: 'Change Password',   icon: KeyRound,   path: '/account/password',  color: 'text-rose-600 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-400' },
  { label: 'Attendance',        icon: CalendarDays, path: '/account/attendance', color: 'text-teal-600 bg-teal-50 dark:bg-teal-900/30 dark:text-teal-400' },
  { label: 'Salary History',    icon: Wallet,     path: '/account/salary',    color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { label: 'My Assets',         icon: Package,    path: '/account/assets',    color: 'text-teal-600 bg-teal-50 dark:bg-teal-900/30 dark:text-teal-400' },
  { label: 'Loan Overview',     icon: Landmark,   path: '/account/loans',     color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400' },
];

function QuickLinksCard({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  return (
    <GlassCard>
      <SectionHeader icon={<Link2 />} title="Quick Links" />
      <div className={CARD_BODY_CLS}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
          {QUICK_LINKS.map((link) => {
            const Icon = link.icon;
            return (
              <button
                key={link.path}
                type="button"
                onClick={() => navigate(link.path)}
                className="flex min-w-0 items-center gap-2 rounded-lg border border-border/40 bg-muted/30 px-2.5 py-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:bg-muted/80"
              >
                <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md', link.color)}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">{link.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </GlassCard>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function AccountPage() {
  const { user, profile, photo_url, roles } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Stable month bounds (computed once)
  const { monthFrom, monthTo, monthLabel } = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const pad = (n: number) => String(n).padStart(2, '0');
    const lastDay = new Date(y, m, 0).getDate();
    return {
      monthFrom: `${y}-${pad(m)}-01`,
      monthTo:   `${y}-${pad(m)}-${pad(lastDay)}`,
      monthLabel: `${MONTH_NAMES[m - 1]} ${y}`,
    };
  }, []);

  // ── Queries ──────────────────────────────────────────────────────────────────

  const balancesQuery = useQuery({
    queryKey: ['leave', 'balances', 'me'],
    queryFn: () => leaveService.getMyBalances(),
  });

  const recentLeavesQuery = useQuery({
    queryKey: ['leave', 'requests', 'me', { limit: 3 }],
    queryFn: () => leaveService.listMyRequests({ limit: 3, page: 1 }),
  });

  const attendanceQuery = useQuery({
    queryKey: ['attendance', 'me', monthFrom, monthTo],
    queryFn: () => attendanceService.listMyAttendance({ date_from: monthFrom, date_to: monthTo, limit: 100 }),
  });

  const salaryQuery = useQuery({
    queryKey: ['payroll', 'salary', 'me'],
    queryFn: () => payrollService.getMySalaryHistory(),
  });

  const assetsQuery = useQuery({
    queryKey: ['assets', 'my', { limit: 3 }],
    queryFn: () => assetsService.listMyAssets({ limit: 3, page: 1 }),
  });

  const documentsQuery = useQuery({
    queryKey: ['staff-documents', 'me', user?.id],
    queryFn: () => staffDocumentsService.listMine(),
    enabled: !!user?.id,
    select: (docs) => docs.slice(0, 5),
  });

  const notifsQuery = useQuery({
    queryKey: ['notifications', { limit: 5 }],
    queryFn: () => notificationsService.list({ limit: 5 }),
  });

  // ── Mutation ─────────────────────────────────────────────────────────────────

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('All notifications marked as read');
    },
    onError: () => toast.error('Failed to mark notifications as read'),
  });

  // ── Derived attendance stats ──────────────────────────────────────────────────

  const attendanceStats = useMemo<AttendanceStats>(() => {
    const records = (attendanceQuery.data as Awaited<ReturnType<typeof attendanceService.listMyAttendance>> | undefined)?.data ?? [];
    return {
      presentDays:  records.filter((r) => r.status === 'present' || r.status === 'work_from_home').length,
      absentDays:   records.filter((r) => r.status === 'absent').length,
      lateDays:     records.filter((r) => r.status === 'late').length,
      totalMinutes: records.reduce((sum, r) => sum + (r.effective_working_minutes ?? 0), 0),
    };
  }, [attendanceQuery.data]);

  // One grid for every section. Cells in the same row stretch to the same height.
  //   phone   (1 col): each card full width, in reading order
  //   tablet  (2 col): Leave Balances full width, then pairs
  //   desktop (3 col): Leave Balances + Recent Leaves | Attendance + Salary | Assets + Documents + Notifications
  return (
    <div className="space-y-3 py-2">
      <ProfileHeroCard
        user={user}
        profile={profile}
        photo_url={photo_url}
        roles={roles}
        navigate={navigate}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        <div className="md:col-span-2">
          <LeaveBalancesSection query={balancesQuery} navigate={navigate} />
        </div>
        <RecentLeaveRequestsCard query={recentLeavesQuery} navigate={navigate} />

        <div className="xl:col-span-2">
          <AttendanceSummaryCard
            query={attendanceQuery}
            stats={attendanceStats}
            monthLabel={monthLabel}
            navigate={navigate}
          />
        </div>
        <SalarySnapshotCard query={salaryQuery} navigate={navigate} />

        <AssignedAssetsCard query={assetsQuery} navigate={navigate} />
        <MyDocumentsCard query={documentsQuery} />
        <RecentNotificationsCard
          query={notifsQuery}
          onMarkAllRead={() => markAllReadMutation.mutate()}
        />
      </div>

      <QuickLinksCard navigate={navigate} />
    </div>
  );
}
